import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaFileArchive,
  FaFileExcel,
  FaFilePdf,
  FaFileWord,
  FaSpinner,
  FaUpload,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/axios";
import useAuth from "../../hooks/useAuthContext";
import "./admPatrocinadoresList.css";
import "./admCartasPeca.css";

const PLACEHOLDER_KEYS = [
  "NOME N",
  "NOME N+1",
  "NOME N+2",
  "NOME N+3",
  "NOME N-1",
  "NOME N-2",
  "NOME N-3",
];

const ZIP_TIMEOUT_MS = 600000;

async function mensagemErroBlob(err, fallback) {
  let mensagem = fallback;
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      const json = JSON.parse(texto);
      mensagem = json?.mensagem || mensagem;
    } catch {
      /* mantém mensagem padrão */
    }
  } else if (data?.mensagem) {
    mensagem = data.mensagem;
  }
  return mensagem;
}

function baixarBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const AdmCartasPeca = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const inputRef = useRef(null);

  const [etapa, setEtapa] = useState("upload");
  const [arquivoNome, setArquivoNome] = useState("");
  const [cartas, setCartas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [indiceAtual, setIndiceAtual] = useState(0);
  const [isAnalisando, setIsAnalisando] = useState(false);
  const [isGerandoZip, setIsGerandoZip] = useState(false);
  const [gerandoIndividual, setGerandoIndividual] = useState(null); // 'pdf' | 'docx' | null
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (usuario && usuario.tipo !== "administracao") {
      navigate("/private/adm");
    }
  }, [usuario, navigate]);

  const cartaAtual = cartas[indiceAtual] || null;

  const placeholdersLista = useMemo(() => {
    if (!cartaAtual?.placeholders) return [];
    return PLACEHOLDER_KEYS.map((chave) => ({
      chave,
      valor: cartaAtual.placeholders[chave] || "",
    }));
  }, [cartaAtual]);

  const resetar = useCallback(() => {
    setEtapa("upload");
    setArquivoNome("");
    setCartas([]);
    setResumo(null);
    setIndiceAtual(0);
    setIsAnalisando(false);
    setIsGerandoZip(false);
    setGerandoIndividual(null);
    setDragOver(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const analisarArquivo = useCallback(
    async (file) => {
      if (!file || isAnalisando || isGerandoZip) return;

      const nome = String(file.name || "").toLowerCase();
      if (!nome.endsWith(".xlsx") && !nome.endsWith(".xls")) {
        toast.error("Envie um arquivo Excel (.xlsx ou .xls).");
        return;
      }

      setArquivoNome(file.name || "arquivo.xlsx");
      setIsAnalisando(true);

      try {
        const formData = new FormData();
        formData.append("arquivo", file);

        const resp = await api.post("/cartas-peca-preview", formData, {
          headers: { "content-type": "multipart/form-data" },
        });

        const dados = resp.data?.dados || [];
        if (!dados.length) {
          toast.error("Nenhuma carta encontrada no arquivo.");
          setArquivoNome("");
          if (inputRef.current) inputRef.current.value = "";
          return;
        }

        setCartas(dados);
        setResumo(resp.data?.resumo || { total: dados.length });
        setIndiceAtual(0);
        setEtapa("preview");
        toast.success(
          resp.data?.mensagem ||
            `${dados.length} carta(s) pronta(s) para pré-visualização.`,
        );
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.mensagem ||
            "Erro ao analisar o arquivo Excel.",
        );
        setArquivoNome("");
        if (inputRef.current) inputRef.current.value = "";
      } finally {
        setIsAnalisando(false);
      }
    },
    [isAnalisando, isGerandoZip],
  );

  const onSelectFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) analisarArquivo(file);
    },
    [analisarArquivo],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (isAnalisando || isGerandoZip) return;
      const file = e.dataTransfer?.files?.[0];
      if (file) analisarArquivo(file);
    },
    [analisarArquivo, isAnalisando, isGerandoZip],
  );

  const irAnterior = useCallback(() => {
    setIndiceAtual((i) => Math.max(0, i - 1));
  }, []);

  const irProximo = useCallback(() => {
    setIndiceAtual((i) => Math.min(cartas.length - 1, i + 1));
  }, [cartas.length]);

  const ocupado = isAnalisando || isGerandoZip || Boolean(gerandoIndividual);

  const gerarIndividual = useCallback(
    async (formato) => {
      if (!cartas.length || ocupado) return;
      const fmt = formato === "docx" ? "docx" : "pdf";
      setGerandoIndividual(fmt);

      try {
        const payload = {
          dados: cartas.map((c) => ({
            nome: c.nome,
            cargo: c.cargo,
            empresa: c.empresa,
            linha: c.linha,
          })),
          indice: indiceAtual,
          formato: fmt,
        };

        const resp = await api.post("/cartas-peca-gerar-individual", payload, {
          responseType: "blob",
          timeout: 120000,
        });

        const contentType = String(resp.headers?.["content-type"] || "");
        if (contentType.includes("application/json")) {
          const texto = await resp.data.text();
          let mensagem = `Erro ao gerar o ${fmt === "docx" ? "Word" : "PDF"}.`;
          try {
            const json = JSON.parse(texto);
            mensagem = json?.mensagem || mensagem;
          } catch {
            /* mantém mensagem padrão */
          }
          toast.error(mensagem);
          return;
        }

        const disposition = resp.headers?.["content-disposition"] || "";
        const match = /filename="?([^"]+)"?/i.exec(disposition);
        const fallbackName =
          fmt === "docx"
            ? `carta_${indiceAtual + 1}.docx`
            : `carta_${indiceAtual + 1}.pdf`;
        const fileName = match?.[1] || fallbackName;
        const mime =
          contentType ||
          (fmt === "docx"
            ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            : "application/pdf");

        baixarBlob(new Blob([resp.data], { type: mime }), fileName);
        toast.success(
          fmt === "docx"
            ? "Word da carta gerado."
            : "PDF da carta gerado.",
        );
      } catch (err) {
        console.error("Erro ao gerar carta individual:", err);
        const mensagem = await mensagemErroBlob(
          err,
          `Erro ao gerar o ${fmt === "docx" ? "Word" : "PDF"}.`,
        );
        toast.error(mensagem);
      } finally {
        setGerandoIndividual(null);
      }
    },
    [cartas, indiceAtual, ocupado],
  );

  const gerarZip = useCallback(async () => {
    if (!cartas.length || ocupado) return;
    setIsGerandoZip(true);

    try {
      const payload = {
        dados: cartas.map((c) => ({
          nome: c.nome,
          cargo: c.cargo,
          empresa: c.empresa,
          linha: c.linha,
        })),
      };

      const resp = await api.post("/cartas-peca-gerar-zip", payload, {
        responseType: "blob",
        timeout: ZIP_TIMEOUT_MS,
      });

      const contentType = String(resp.headers?.["content-type"] || "");
      if (contentType.includes("application/json")) {
        const texto = await resp.data.text();
        let mensagem = "Erro ao gerar o ZIP das cartas.";
        try {
          const json = JSON.parse(texto);
          mensagem = json?.mensagem || mensagem;
        } catch {
          /* mantém mensagem padrão */
        }
        toast.error(mensagem);
        return;
      }

      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disposition);
      const fileName =
        match?.[1] ||
        `cartas_peca_sbf2026_${new Date().toISOString().slice(0, 10)}.zip`;

      const blob = new Blob([resp.data], {
        type: contentType || "application/zip",
      });
      baixarBlob(blob, fileName);
      toast.success(
        `ZIP gerado com ${cartas.length} carta(s) (PDF + DOCX).`,
      );
    } catch (err) {
      console.error("Erro ao gerar ZIP das cartas:", err);
      const mensagem = await mensagemErroBlob(
        err,
        "Erro ao gerar o ZIP das cartas.",
      );
      toast.error(mensagem);
    } finally {
      setIsGerandoZip(false);
    }
  }, [cartas, ocupado]);

  if (usuario?.tipo !== "administracao") {
    return null;
  }

  return (
    <div className="container-adm-main">
      <div className="apl-page acp-page">
        <header className="apl-header" aria-label="Cabeçalho cartas peça">
          <div className="apl-header-text">
            <h1>Cartas peça</h1>
            <p>
              Envie a planilha Excel, confira a pré-visualização de cada carta e
              gere o ZIP com os PDFs personalizados.
            </p>
          </div>

          <div className="apl-toolbar acp-toolbar">
            {etapa === "preview" ? (
              <>
                <button
                  type="button"
                  className="apl-btn apl-btn--secondary"
                  onClick={resetar}
                  disabled={isGerandoZip || Boolean(gerandoIndividual)}
                >
                  <FaFileExcel size={14} aria-hidden />
                  Novo Excel
                </button>
                <button
                  type="button"
                  className="apl-btn apl-btn--primary"
                  onClick={gerarZip}
                  disabled={ocupado || !cartas.length}
                  title="Gerar ZIP com PDFs"
                >
                  {isGerandoZip ? (
                    <FaSpinner className="fa-spin" size={14} aria-hidden />
                  ) : (
                    <FaFileArchive size={14} aria-hidden />
                  )}
                  {isGerandoZip
                    ? "Gerando ZIP…"
                    : `Gerar ZIP (${cartas.length})`}
                </button>
              </>
            ) : null}

            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              title="Voltar"
              disabled={ocupado}
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {etapa === "upload" ? (
            <section
              className="apl-card acp-upload-card"
              aria-label="Upload Excel"
            >
              <p className="acp-hint">
                Colunas esperadas: <strong>Nome</strong>,{" "}
                <strong>Cargo</strong> e <strong>Empresa</strong> (ou
                equivalentes na primeira linha). Formatos: .xlsx ou .xls · até
                10 MB. Linhas sem nome geram espaço em branco na carta para
                escrita à mão.
              </p>

              <div
                className={`acp-dropzone${
                  isAnalisando ? " acp-dropzone--busy" : ""
                }${dragOver ? " acp-dropzone--drag" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isAnalisando) setDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOver(false);
                }}
                onDrop={onDrop}
                onClick={() => !isAnalisando && inputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    inputRef.current?.click();
                  }
                }}
                aria-label="Selecionar arquivo Excel"
              >
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="acp-file-input"
                  onChange={onSelectFile}
                  disabled={isAnalisando}
                />

                {isAnalisando ? (
                  <>
                    <FaSpinner
                      className="fa-spin acp-dropzone-icon"
                      size={28}
                      aria-hidden
                    />
                    <span className="acp-dropzone-title">
                      Analisando planilha…
                    </span>
                    <span className="acp-dropzone-sub">
                      {arquivoNome || "Aguarde"}
                    </span>
                  </>
                ) : (
                  <>
                    <FaUpload
                      className="acp-dropzone-icon"
                      size={28}
                      aria-hidden
                    />
                    <span className="acp-dropzone-title">
                      Arraste o Excel ou clique para selecionar
                    </span>
                    <span className="acp-dropzone-sub">
                      Pré-visualização das cartas peça antes de gerar o ZIP
                    </span>
                  </>
                )}
              </div>
            </section>
          ) : (
            <>
              <div className="acp-resumo" role="status">
                <span>
                  Arquivo: <strong>{arquivoNome || "—"}</strong>
                </span>
                <span>
                  Total: <strong>{resumo?.total ?? cartas.length}</strong>{" "}
                  participante(s)
                </span>
                <span>
                  Carta{" "}
                  <strong>
                    {indiceAtual + 1} / {cartas.length}
                  </strong>
                </span>
              </div>

              <div className="acp-preview-grid">
                <aside
                  className="apl-card acp-lista"
                  aria-label="Lista de participantes"
                >
                  <div className="acp-lista-header">
                    <h2>Participantes</h2>
                  </div>
                  <ul className="acp-lista-ul">
                    {cartas.map((c, idx) => (
                      <li key={`${c.linha}-${idx}`}>
                        <button
                          type="button"
                          className={`acp-lista-item${
                            idx === indiceAtual
                              ? " acp-lista-item--active"
                              : ""
                          }`}
                          onClick={() => setIndiceAtual(idx)}
                          disabled={ocupado}
                        >
                          <span className="acp-lista-idx">{idx + 1}</span>
                          <span className="acp-lista-meta">
                            <span className="acp-lista-nome">
                              {c.nome?.trim()
                                ? c.nome
                                : "Sem nome (espaço à mão)"}
                            </span>
                            <span className="acp-lista-sub">
                              {[c.cargo, c.empresa]
                                .filter(Boolean)
                                .join(" · ") || `Linha ${c.linha}`}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </aside>

                <section
                  className="apl-card acp-carta-panel"
                  aria-label="Pré-visualização da carta"
                >
                  <div className="acp-carta-nav">
                    <button
                      type="button"
                      className="apl-btn apl-btn--ghost"
                      onClick={irAnterior}
                      disabled={indiceAtual <= 0 || ocupado}
                      title="Carta anterior"
                    >
                      <FaArrowLeft size={14} aria-hidden />
                      Anterior
                    </button>
                    <span className="acp-carta-nav-label">
                      {cartaAtual?.nome?.trim()
                        ? cartaAtual.nome
                        : "Sem nome (espaço à mão)"}
                    </span>
                    <button
                      type="button"
                      className="apl-btn apl-btn--ghost"
                      onClick={irProximo}
                      disabled={
                        indiceAtual >= cartas.length - 1 || ocupado
                      }
                      title="Próxima carta"
                    >
                      Próxima
                      <FaArrowRight size={14} aria-hidden />
                    </button>
                  </div>

                  {cartaAtual ? (
                    <div className="acp-letter-sheet">
                      <p className="acp-letter-saudacao">
                        {cartaAtual.textoSaudacao}
                      </p>
                      <p className="acp-letter-jornada">
                        {cartaAtual.textoJornada}
                      </p>

                      <div className="acp-placeholders">
                        <h3>Placeholders</h3>
                        <table className="acp-placeholders-table">
                          <thead>
                            <tr>
                              <th>Chave</th>
                              <th>Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {placeholdersLista.map((row) => (
                              <tr key={row.chave}>
                                <td>
                                  <code>{`{{${row.chave}}}`}</code>
                                </td>
                                <td>{row.valor || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="apl-empty">
                      <p className="apl-empty-title">
                        Nenhuma carta selecionada
                      </p>
                    </div>
                  )}

                  <footer className="acp-carta-footer">
                    <button
                      type="button"
                      className="apl-btn apl-btn--ghost"
                      onClick={() => gerarIndividual("docx")}
                      disabled={!cartaAtual || ocupado}
                      title="Baixar Word desta carta"
                    >
                      {gerandoIndividual === "docx" ? (
                        <FaSpinner className="fa-spin" size={14} aria-hidden />
                      ) : (
                        <FaFileWord size={14} aria-hidden />
                      )}
                      {gerandoIndividual === "docx"
                        ? "Gerando Word…"
                        : "Gerar Word"}
                    </button>
                    <button
                      type="button"
                      className="apl-btn apl-btn--primary"
                      onClick={() => gerarIndividual("pdf")}
                      disabled={!cartaAtual || ocupado}
                      title="Baixar PDF desta carta"
                    >
                      {gerandoIndividual === "pdf" ? (
                        <FaSpinner className="fa-spin" size={14} aria-hidden />
                      ) : (
                        <FaFilePdf size={14} aria-hidden />
                      )}
                      {gerandoIndividual === "pdf"
                        ? "Gerando PDF…"
                        : "Gerar PDF"}
                    </button>
                  </footer>
                </section>
              </div>

              {isGerandoZip || gerandoIndividual ? (
                <div
                  className="acp-generating"
                  role="status"
                  aria-live="polite"
                >
                  <FaSpinner className="fa-spin" size={22} aria-hidden />
                  <span>
                    {isGerandoZip
                      ? "Gerando ZIP com PDFs… isso pode levar alguns minutos."
                      : gerandoIndividual === "docx"
                        ? "Gerando Word da carta selecionada…"
                        : "Gerando PDF da carta selecionada…"}
                  </span>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmCartasPeca;